import { useMemo, useRef, useState } from "react";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Scene3D from "./components/Scene3D";
import FovCanvas from "./components/FovCanvas";
import { DEFAULT_PARAMS, DEFAULT_UI_STATE, PARAM_RANGES, buildAngles } from "./geometry/defaults";
import { computeDefaultGeom, computePlanarGeom, computeGimballLockGeom } from "./geometry/vxGeom";
import { commonFovAtZ, estimateGridHalf } from "./geometry/fov";
import { useWithinFov } from "./geometry/useWithinFov";
import "./App.css";

function App() {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [showInclined, setShowInclined] = useState(DEFAULT_UI_STATE.showInclined);
  const [showCoplanar, setShowCoplanar] = useState(DEFAULT_UI_STATE.showCoplanar);
  const [showGimbal, setShowGimbal] = useState(DEFAULT_UI_STATE.showGimbal);
  const [theta, setTheta] = useState(DEFAULT_UI_STATE.theta);
  const [showAllFootprints, setShowAllFootprints] = useState(DEFAULT_UI_STATE.showAllFootprints);
  const [showVolume, setShowVolume] = useState(DEFAULT_UI_STATE.showVolume);
  const [showXraySurface, setShowXraySurface] = useState(DEFAULT_UI_STATE.showXraySurface);
  const sceneRef = useRef(null);

  const updateParam = (key, value) =>
    setParams((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "angleStart" || key === "angleEnd") {
        const lo = Math.min(next.angleStart, next.angleEnd);
        const hi = Math.max(next.angleStart, next.angleEnd);
        setTheta((t) => Math.min(hi, Math.max(lo, t)));
      }
      return next;
    });

  const resetAll = () => {
    setParams(DEFAULT_PARAMS);
    setShowInclined(DEFAULT_UI_STATE.showInclined);
    setShowCoplanar(DEFAULT_UI_STATE.showCoplanar);
    setShowGimbal(DEFAULT_UI_STATE.showGimbal);
    setTheta(DEFAULT_UI_STATE.theta);
    setShowAllFootprints(DEFAULT_UI_STATE.showAllFootprints);
    setShowVolume(DEFAULT_UI_STATE.showVolume);
    setShowXraySurface(DEFAULT_UI_STATE.showXraySurface);
  };

  const suggestVolRes = () => {
    const suggested = (params.detPitch * params.sod) / params.sdd;
    const { min, max } = PARAM_RANGES.volRes;
    updateParam("volRes", Math.min(max, Math.max(min, suggested)));
  };

  const angles = useMemo(
    () => buildAngles(params.numAngles, params.angleStart, params.angleEnd),
    [params.numAngles, params.angleStart, params.angleEnd]
  );

  const halfU = params.detWidth / 2;
  const halfV = params.detHeight / 2;

  const geomParams = useMemo(
    () => ({
      sod: params.sod,
      sdd: params.sdd,
      tiltX: params.tiltX,
      tiltY: params.tiltY,
      detPitch: params.detPitch,
      offsetU: params.offsetU,
      offsetV: params.offsetV,
      angles,
    }),
    [params, angles]
  );

  const inclinedVecs = useMemo(() => computeDefaultGeom(geomParams), [geomParams]);
  const coplanarVecs = useMemo(() => computePlanarGeom(geomParams), [geomParams]);
  const gimbalVecs = useMemo(() => computeGimballLockGeom(geomParams), [geomParams]);

  const gridHalf = useMemo(
    () => estimateGridHalf(params.sod, params.sdd, params.detWidth, params.detPitch),
    [params.sod, params.sdd, params.detWidth, params.detPitch]
  );

  const fovResults = useMemo(() => {
    const inclined = commonFovAtZ(inclinedVecs, halfU, halfV, 0);
    const coplanar = commonFovAtZ(coplanarVecs, halfU, halfV, 0);
    const gimbal = commonFovAtZ(gimbalVecs, halfU, halfV, 0);
    return { inclined, coplanar, gimbal };
  }, [inclinedVecs, coplanarVecs, gimbalVecs, halfU, halfV]);

  // Coplanar is the reference setup: every other setup's area is shown as a
  // signed % delta relative to it (negative = smaller FOV than coplanar).
  const relativeToCoplanar = useMemo(() => {
    const base = fovResults.coplanar.area;
    const pct = (area) => (base > 0 ? ((area - base) / base) * 100 : 0);
    return {
      inclined: pct(fovResults.inclined.area),
      gimbal: pct(fovResults.gimbal.area),
    };
  }, [fovResults]);

  // Whether the reconstruction volume fits entirely within each setup's
  // usable FOV across its full height. Runs off the main thread (one Web
  // Worker per setup) and is debounced ~200ms after inputs settle, so it
  // can never freeze the UI even at numAngles=2000, and a burst of rapid
  // slider changes just discards stale in-flight results instead of queuing.
  const vecsByKey = useMemo(
    () => ({ inclined: inclinedVecs, coplanar: coplanarVecs, gimbal: gimbalVecs }),
    [inclinedVecs, coplanarVecs, gimbalVecs]
  );
  const withinFovBySetup = useWithinFov({
    vecsByKey,
    halfU,
    halfV,
    volRes: params.volRes,
    volX: params.volX,
    volY: params.volY,
    volZ: params.volZ,
    offsetVolX: params.offsetVolX,
    offsetVolY: params.offsetVolY,
    offsetVolZ: params.offsetVolZ,
    enabled: showVolume,
  });

  // Canvas status label reflects the worst case across visible setups only.
  const withinFov =
    (!showInclined || withinFovBySetup.inclined) &&
    (!showCoplanar || withinFovBySetup.coplanar) &&
    (!showGimbal || withinFovBySetup.gimbal);

  return (
    <div className="app-shell">
      <Header />
      <div className="app-body">
        <Sidebar
          params={params}
          onParamChange={updateParam}
          showInclined={showInclined}
          showCoplanar={showCoplanar}
          showGimbal={showGimbal}
          onToggleInclined={() => setShowInclined((v) => !v)}
          onToggleCoplanar={() => setShowCoplanar((v) => !v)}
          onToggleGimbal={() => setShowGimbal((v) => !v)}
          fovResults={fovResults}
          relativeToCoplanar={relativeToCoplanar}
          theta={theta}
          onThetaChange={setTheta}
          onReset={resetAll}
          showAllFootprints={showAllFootprints}
          onToggleAllFootprints={() => setShowAllFootprints((v) => !v)}
          showVolume={showVolume}
          onToggleVolume={() => setShowVolume((v) => !v)}
          showXraySurface={showXraySurface}
          onToggleXraySurface={() => setShowXraySurface((v) => !v)}
          onFocusFov={() => sceneRef.current?.focusFov()}
          onFocusOverall={() => sceneRef.current?.focusOverall()}
          onFocusDirection={(direction) => sceneRef.current?.focusDirection(direction)}
          onSuggestVolRes={suggestVolRes}
        />
        <div className="canvas-column">
          <div className="canvas-pane canvas-pane-top">
            <Scene3D
              ref={sceneRef}
              inclinedVecs={inclinedVecs}
              coplanarVecs={coplanarVecs}
              gimbalVecs={gimbalVecs}
              showInclined={showInclined}
              showCoplanar={showCoplanar}
              showGimbal={showGimbal}
              halfU={halfU}
              halfV={halfV}
              theta={theta}
              numAngles={params.numAngles}
              angleStart={params.angleStart}
              angleEnd={params.angleEnd}
              volRes={params.volRes}
              volX={params.volX}
              volY={params.volY}
              volZ={params.volZ}
              offsetVolX={params.offsetVolX}
              offsetVolY={params.offsetVolY}
              offsetVolZ={params.offsetVolZ}
              showVolume={showVolume}
              showXraySurface={showXraySurface}
              withinFov={withinFov}
              sdd={params.sdd}
            />
          </div>
          <div className="canvas-pane canvas-pane-bottom">
            <FovCanvas
              fovResults={fovResults}
              gridHalf={gridHalf}
              showInclined={showInclined}
              showCoplanar={showCoplanar}
              showGimbal={showGimbal}
              showAllFootprints={showAllFootprints}
              volRes={params.volRes}
              volX={params.volX}
              volY={params.volY}
              offsetVolX={params.offsetVolX}
              offsetVolY={params.offsetVolY}
              showVolume={showVolume}
              withinFov={withinFov}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
